<script lang="ts">
  import Play from '~icons/mdi/play';
  import Pause from '~icons/mdi/pause';
  import VolumeHigh from '~icons/mdi/volume-high';
  import VolumeOff from '~icons/mdi/volume-off';
  import ActionButton from '../components/ActionButton.svelte';
  import { pronunciationQuery } from '~/lib/queries.svelte';

  type Props = {
    word: string;
  };
  const { word }: Props = $props();

  // audio is only looked up once the user asks for it
  let requested = $state(false);
  let selected = $state(0);
  let playing = $state(false);
  let progress = $state(0);
  // play as soon as the lookup triggered by the first tap resolves
  let playWhenReady = $state(false);
  let audio: HTMLAudioElement | undefined = $state();

  const pronunciation = $derived(pronunciationQuery(word, requested));
  const recordings = $derived(pronunciation.data ?? []);
  const current = $derived(recordings[selected]);
  const unavailable = $derived(
    pronunciation.isError || (pronunciation.isSuccess && recordings.length === 0),
  );

  const play = async () => {
    if (!audio) return;
    try {
      await audio.play();
    } catch {
      // autoplay can be blocked once the tap's gesture has expired
      playing = false;
    }
  };

  const toggle = () => {
    if (!requested || !current) {
      requested = true;
      playWhenReady = true;
      return;
    }
    if (playing) {
      audio?.pause();
    } else {
      play();
    }
  };

  const select = (index: number) => {
    selected = index;
    playWhenReady = true;
  };

  $effect(() => {
    // src changes reset the element, so wait for it before playing
    if (playWhenReady && current && audio?.src === current.src) {
      playWhenReady = false;
      play();
    }
  });

  // reset between words
  $effect(() => {
    word;
    return () => {
      audio?.pause();
      requested = false;
      selected = 0;
      progress = 0;
      playWhenReady = false;
    };
  });
</script>

<div class="pronunciation">
  {#if unavailable}
    <span class="unavailable">
      <VolumeOff />
      No pronunciation available
    </span>
  {:else}
    <ActionButton
      iconOnly
      onClick={toggle}
      disabled={pronunciation.isFetching}
    >
      {#if playing}
        <Pause />
      {:else if requested}
        <Play />
      {:else}
        <VolumeHigh />
      {/if}
    </ActionButton>
    <div
      class="track"
      class:loading={pronunciation.isFetching}
      role="progressbar"
      aria-label="Pronunciation playback"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(progress * 100)}
    >
      <div class="fill" style:width="{progress * 100}%"></div>
    </div>
    {#if recordings.length > 1}
      <div class="accents">
        {#each recordings as recording, i (recording.src)}
          <button
            class="accent"
            class:selected={i === selected}
            onclick={() => select(i)}
          >
            {recording.label || 'EN'}
          </button>
        {/each}
      </div>
    {:else if current?.label}
      <span class="accent static">{current.label}</span>
    {/if}
  {/if}
  {#if current}
    <audio
      bind:this={audio}
      src={current.src}
      preload="auto"
      onplay={() => (playing = true)}
      onpause={() => (playing = false)}
      onended={() => {
        playing = false;
        progress = 0;
      }}
      ontimeupdate={(e) => {
        const { currentTime, duration } = e.currentTarget;
        progress = duration ? currentTime / duration : 0;
      }}
    ></audio>
  {/if}
</div>

<style lang="scss">
  .pronunciation {
    display: flex;
    align-items: center;
    gap: 8px;
    // fills the row beside the phonetic text, wrapping below it when narrow
    flex: 1;
    min-width: 160px;
    max-width: 280px;
  }
  .track {
    position: relative;
    flex: 1;
    height: 4px;
    border-radius: 2px;
    background: rgba(128, 128, 128, 0.3);
    overflow: hidden;
    &.loading .fill {
      width: 30% !important;
      animation: loading 1s ease-in-out infinite alternate;
    }
  }
  .fill {
    height: 100%;
    background: currentColor;
    opacity: 0.8;
    transition: width 0.25s linear;
  }
  @keyframes loading {
    from {
      transform: translateX(-100%);
    }
    to {
      transform: translateX(333%);
    }
  }
  .accents {
    display: flex;
    gap: 4px;
  }
  .accent {
    font: inherit;
    font-size: 0.75em;
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 4px;
    border: 1px solid rgba(128, 128, 128, 0.5);
    background: none;
    color: inherit;
    opacity: 0.6;
    cursor: pointer;
    &.selected {
      opacity: 1;
      border-color: currentColor;
    }
    &.static {
      cursor: default;
    }
  }
  .unavailable {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.85em;
    opacity: 0.6;
  }
</style>
